import React, { useState, useEffect } from 'react';
import { Container, Grid, Paper, Typography, Box, Card, CardContent, Divider, useTheme, Button, useMediaQuery } from '@mui/material';
import { SupervisorAccount, Lightbulb, CloudUpload, Analytics, Add, Verified, TrendingUp, Assignment, School, CheckCircle } from '@mui/icons-material';
import { apiService } from '../../apiServices/ApiService';
import { APIStudentDashboard } from '../../apiServices/APIs'; // Ensure this returns aggregate counts
import { getUserDetails } from '../../utils/helpers';
import LoadingState from '../../components/LoadingState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import { url_studentTopicUpload, url_studentWorkUpload } from '../../components/appPath';
import { useNavigate } from 'react-router-dom';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%', borderRadius: 3, borderLeft: `6px solid ${color}` }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 2, bgcolor: `${color}15`, borderRadius: '50%', color: color }}>{icon}</Box>
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{title}</Typography>
            </Box>
        </CardContent>
    </Card>
);

const StudentDashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ totalSupervisors: 0, totalTopics: 0, totalWorks: 0 });
    const studentId = getUserDetails("Id");

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Assuming your backend API returns an object with these specific counts
                const response = await apiService.listDashboardData(APIStudentDashboard, studentId);
                if (response.success) {
                    setData({
                        totalSupervisors: response.data.total_supervisors || 0,
                        totalTopics: response.data.total_topics || 0,
                        totalWorks: response.data.total_works || 0,
                    });
                }
            } catch (err) {
                console.error("Dashboard fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [studentId]);

    const handleWorkNavigation = () =>{navigate(url_studentWorkUpload);}
    const handleTopicNavigation = ()=>{navigate(url_studentTopicUpload);}



    if (loading) return <LoadingState message="Loading your dashboard..." />;

    return (
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <StudentBar notificationCount={5} />

                    {/* Welcome Header */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3, 
                        color:`${theme.palette.primary.main}`
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Box>
                                <Typography  sx={{fontWeight: 'bold',mb: 1,}}>
                                    Welcome back, {getUserDetails("userName") || 'Researcher'} !
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    {getUserDetails("university") || 'University'} • Registration: {getUserDetails("registrationId") || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {/* <Chip 
                                    icon={student.status === 'active' ? <CheckCircle /> : <Warning />}
                                    label={`Status: ${(student.status || 'active').toUpperCase()}`}
                                    sx={{ bgcolor: `${theme.palette.primary.main}`, color: 'white' }}
                                    size={isMobile ? "small" : "medium"}
                                /> */}
                                {/* <Chip 
                                    icon={<Add/>}
                                    label={"Upload Work"}
                                    sx={{ bgcolor: `${theme.palette.success.dark}`, color: 'white' }}
                                    size={isMobile ? "small" : "mesdium"}
                                /> */}
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<Add />}
                                    onClick={handleTopicNavigation}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                >
                                    Submit Topic
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Add />}
                                    onClick={handleWorkNavigation}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    {isMobile ? 'Upload' : 'Upload Work'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <StatCard title="Assigned Supervisors" value={data.totalSupervisors} icon={<SupervisorAccount />} color={theme.palette.secondary.main} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard title="Research Topics" value={data.totalTopics} icon={<Lightbulb />} color={theme.palette.warning.main} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard title="Total Works Uploaded" value={data.totalWorks} icon={<CloudUpload />} color={theme.palette.success.main} />
                </Grid>
            </Grid>

            <Paper sx={{ mt: 4, p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Analytics /> Quick Analytics
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography color="text.secondary">
                    Your research activity is being tracked. You currently have <b>{data.totalWorks}</b> documents in the system. 
                    Ensure your profile is updated and your supervisor has reviewed your recent uploads.
                </Typography>
            </Paper>
        </FullWidthContainer>
    );
};

export default StudentDashboard;